import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import sliderService, {
  LoadedSlider,
  PublicSlide,
  PublicSponsor,
  autoSlideMs,
  buildSlideImageUrl,
  isExternalLink,
  parseAspectRatio,
  slideImageFor,
  toInternalPath,
  visibleSlides,
} from '../../services/sliderService';
import { t as tr } from '../../i18n';

const DESKTOP_QUERY = '(min-width: 1024px)';

/** Desktop-Erkennung (Tailwind lg) – reagiert auf Resize. */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY).matches : true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return isDesktop;
}

/** Light-Theme-Erkennung über `html.light` (Theme-Toggle der Web-App). */
function useIsLightTheme(): boolean {
  const read = () => typeof document !== 'undefined' && document.documentElement.classList.contains('light');
  const [isLight, setIsLight] = useState<boolean>(read);
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const obs = new MutationObserver(() => setIsLight(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isLight;
}

const ACCENT = '#3fced1';

export interface DynamicSliderProps {
  /** Location aus dem Slider-CMS (gleiche Location = gleiche Stelle in Web und App) */
  location: string;
  className?: string;
  /** Fallback-Seitenverhältnis, wenn der Slide keins hat */
  fallbackRatio?: string;
  /** Meldet die Zahl nutzbarer Slides (0 = Slider rendert nichts) – für Layout-Anpassungen im Parent */
  onSlidesChange?: (count: number) => void;
  /**
   * Vorschau-Modus (Admin-Slide-Formular, 2026-09-27): dieser Slide wird statt der geladenen
   * Slides gerendert – derselbe Renderer wie live, keine zweite Implementierung. Links sind inert.
   */
  previewSlide?: PublicSlide | null;
  /** Vorschau: Sponsoren für Sponsor-Slides */
  previewSponsors?: PublicSponsor[];
  /** Vorschau: Viewport erzwingen (Desktop ≥ 1024 px / Mobile) statt window.matchMedia */
  previewViewport?: 'desktop' | 'mobile';
  /** Vorschau: helles Theme erzwingen (zeigt das Light-Bild); undefined = aktuelles Theme */
  previewLight?: boolean;
}

/**
 * Erster Web-Renderer des Slider-CMS (2026-09-15). Lädt Slider + aktive Slides einer
 * Location, rendert NICHTS solange geladen wird oder nichts da ist (kein Layout-Sprung
 * für leere Werbeflächen). Manual-Slides: Bild im Slide-Seitenverhältnis, optional
 * Titel/Untertitel/Button-Overlay, ganzer Slide verlinkt. Sponsor-Slides: Logos auf Weiß.
 * Auto-Slide nach autoSlideDuration (Default 5 s), Pause bei Hover, Pfeile + Punkte ab 2 Slides.
 */
const DynamicSlider: React.FC<DynamicSliderProps> = ({
  location, className = '', fallbackRatio = '16:9', onSlidesChange,
  previewSlide = null, previewSponsors, previewViewport, previewLight,
}) => {
  // undefined = noch nicht geladen, null = geladen, aber nichts zu zeigen
  const [loadedData, setLoadedData] = useState<LoadedSlider | null | undefined>(undefined);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const notified = useRef<number | null>(null);
  const detectedDesktop = useIsDesktop();
  const detectedLight = useIsLightTheme();
  const isPreview = previewSlide != null;
  const isDesktop = previewViewport ? previewViewport === 'desktop' : detectedDesktop;
  const isLight = previewLight ?? detectedLight;

  useEffect(() => {
    if (isPreview) return; // Vorschau lädt nichts
    let cancelled = false;
    sliderService.loadByLocation(location).then((loaded) => {
      if (cancelled) return;
      setLoadedData(loaded);
      setIndex(0);
    });
    return () => { cancelled = true; };
  }, [location, isPreview]);

  const previewData = useMemo<LoadedSlider | null>(() => (previewSlide ? {
    slider: { id: 'preview', name: tr('web.component.dynamic-slider.slider'), location, active: true, order: 0, autoSlideDuration: null },
    slides: [previewSlide],
    sponsors: previewSponsors ?? [],
  } : null), [previewSlide, previewSponsors, location]);
  const data = isPreview ? previewData : loadedData;

  // 2026-09-15: desktopOnly-Slides nur auf Desktop; Zahl der sichtbaren Slides an den Parent melden
  const slides = data ? visibleSlides(data.slides, { isDesktop }) : [];
  const count = slides.length;
  useEffect(() => {
    if (data === undefined) return;
    if (notified.current !== count) {
      notified.current = count;
      onSlidesChange?.(count);
    }
    setIndex((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
    // onSlidesChange bewusst nicht in den Deps (Parent-Callbacks sind oft nicht stabil)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, count]);
  const go = useCallback((next: number) => {
    if (count === 0) return;
    setIndex(((next % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count < 2 || paused) return;
    const t = window.setInterval(() => go(index + 1), autoSlideMs(data?.slider.autoSlideDuration));
    return () => window.clearInterval(t);
  }, [count, paused, index, go, data?.slider.autoSlideDuration]);

  if (data == null || count === 0) return null;

  const current = slides[Math.min(index, count - 1)];
  const ratio = parseAspectRatio(current.aspectRatio ?? fallbackRatio, parseAspectRatio(fallbackRatio));

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 ${className}`}
      style={{ aspectRatio: String(ratio) }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription={tr('web.component.dynamic-slider.slider')}
      aria-label={data.slider.name}
      data-testid="dynamic-slider"
      data-preview={isPreview ? previewViewport ?? 'auto' : undefined}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-500 ${i === index ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          aria-hidden={i !== index}
        >
          <SlideView slide={slide} sponsors={data.sponsors} isLight={isLight} inert={isPreview} />
        </div>
      ))}

      {count > 1 && (
        <>
          <button type="button" aria-label={tr('web.component.dynamic-slider.vorheriger-slide')} onClick={() => go(index - 1)}
            className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70">
            <ChevronLeft size={18} />
          </button>
          <button type="button" aria-label={tr('web.component.dynamic-slider.naechster-slide')} onClick={() => go(index + 1)}
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70">
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {slides.map((s, i) => (
              <button key={s.id} type="button" aria-label={tr('web.component.dynamic-slider.slide', { i: i + 1 })} onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4' : 'w-1.5 bg-white/50'}`}
                style={i === index ? { backgroundColor: ACCENT } : undefined} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const LinkWrap: React.FC<{ href?: string | null; label: string; inert?: boolean; children: React.ReactNode }> = ({ href, label, inert, children }) => {
  const url = (href ?? '').trim();
  // Vorschau: kein Navigieren aus dem Admin-Formular heraus (optisch identisch)
  if (!url || inert) return <div className="block h-full w-full" aria-label={label}>{children}</div>;
  if (isExternalLink(url)) {
    return <a href={url} target="_blank" rel="noopener noreferrer sponsored" aria-label={label} className="block h-full w-full">{children}</a>;
  }
  return <Link to={toInternalPath(url)} aria-label={label} className="block h-full w-full">{children}</Link>;
};

const SlideView: React.FC<{ slide: PublicSlide; sponsors: LoadedSlider['sponsors']; isLight: boolean; inert?: boolean }> = ({ slide, sponsors, isLight, inert }) => {
  if (slide.type === 'sponsor') {
    const ids = new Set(slide.sponsorIds ?? []);
    const list = sponsors.filter((s) => ids.has(s.id) && s.active !== false);
    return (
      <div className="flex h-full w-full items-center justify-center bg-white p-4">
        <div className={`grid h-full w-full gap-4 ${list.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {list.map((s) => {
            const img = buildSlideImageUrl(s.image);
            const inner = img ? <img src={img} alt={s.name ?? tr('web.component.dynamic-slider.sponsor')} className="h-full w-full object-contain" loading="lazy" />
              : <span className="text-sm font-bold text-zinc-800">{s.name}</span>;
            return (
              <div key={s.id} className="flex items-center justify-center">
                {s.url && !inert ? <a href={s.url} target="_blank" rel="noopener noreferrer sponsored" className="flex h-full w-full items-center justify-center">{inner}</a> : inner}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  const img = buildSlideImageUrl(slideImageFor(slide, isLight));
  const hasText = Boolean(slide.title || slide.subtitle || slide.buttonText);
  return (
    <LinkWrap href={slide.linkUrl} label={slide.title || tr('web.component.dynamic-slider.slide-oeffnen')} inert={inert}>
      {img && <img src={img} alt={slide.title || ''} className="h-full w-full object-cover" loading="lazy" />}
      {hasText && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-10">
          {slide.title && <p className="text-base font-extrabold leading-tight text-white">{slide.title}</p>}
          {slide.subtitle && <p className="mt-0.5 text-sm text-gray-200">{slide.subtitle}</p>}
          {slide.buttonText && (
            <span className="mt-2 inline-flex rounded-lg px-3 py-1.5 text-xs font-bold text-black" style={{ backgroundColor: ACCENT }}>{slide.buttonText}</span>
          )}
        </div>
      )}
    </LinkWrap>
  );
};

export default DynamicSlider;
